import type { StateCreator } from 'zustand'

import {
  type CombinacaoDeCorte,
  type Eixo,
  type PlanoDeCorte,
  alteracaoPermitida,
  aplicarCombinacao,
  doEixo,
  nomeDaCombinacao,
  ROTULO_EIXO,
} from '@viewer/section-box/planosDeCorte'
import { lerJanela } from '@viewer/section-box/janelaSectionBox'
import {
  gravarEstiloBase,
  gravarMostrarCaixa,
  gravarPreenchimento,
  lerEstiloBase,
  lerMostrarCaixa,
  lerPreenchimento,
} from '@viewer/section-box/mostrarCaixa'
import {
  ESTILO_BASE_POR_OMISSAO,
  REGRAS_SUGERIDAS,
  type EstiloDeCorte,
  type RegraDeCorte,
  type TipoDeRegra,
} from '@viewer/section-box/estilosDeCorte'
import { lerLentes } from '@viewer/lens/lensRules'

/** Estado do PAINEL da Section box, independente da caixa estar ligada:
 *  fechar o painel não desliga a caixa. */
export type SectionPanelState = 'aberto' | 'minimizado' | 'fechado'

export type Vec3 = [number, number, number]

export interface SectionOrientation {
  origin: Vec3
  axisX: Vec3
  axisY: Vec3
  axisZ: Vec3
}

// Box extents along the OBB local axes (relative to origin). Identity
// orientation + extents = world-axis-aligned box. After alignment to
// a picked face, axisZ aligns with the face normal and extents are
// recomputed in the new local frame.
export interface SectionLocalExtents {
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

/** O que o preenchimento dos cortes está a fazer, para o painel. */
export interface PreenchimentoInfo {
  aCalcular: boolean
  /** Elementos preenchidos (somando os planos). */
  elementos: number
  /** Elementos cortados que ficaram sem preenchimento (contorno aberto). */
  abertos: number
}

export interface SectionSlice {
  sectionEnabled: boolean
  sectionOrientation: SectionOrientation | null
  sectionExtents: SectionLocalExtents | null
  sectionExtentRange: SectionLocalExtents | null
  sectionAlignMode: boolean
  /** O painel: aberto, minimizado numa barra, ou fechado (a caixa
   *  continua a cortar). */
  sectionPanel: SectionPanelState
  /** Desenhar a caixa (arestas, faces e pegas). Escondida, o corte
   *  continua e não há pegas a apanhar cliques. Preferência guardada
   *  por browser. */
  sectionMostrarCaixa: boolean
  /** O painel está numa janela do browser à parte. Não se guarda: o
   *  browser só abre janelas a seguir a um gesto da utilizadora, por isso
   *  reabrir a aplicação com isto ligado dava uma janela bloqueada. */
  sectionPanelDestacado: boolean

  /** Planos de corte soltos, independentes da caixa.
   *
   *  A caixa é seis planos presos uns aos outros e serve para isolar um
   *  pedaço. Estes são livres: um corte pela fachada, outro pelo piso,
   *  outro por uma parede inclinada, os três ao mesmo tempo. */
  planosDeCorte: PlanoDeCorte[]
  /** À espera de um clique numa face para criar um plano paralelo a
   *  ela. Separado do `sectionAlignMode`, que é o da caixa: são dois
   *  gestos diferentes e confundi-los dava cortes onde ninguém pediu. */
  planoPorFace: boolean
  /** Combinações de planos com nome, para ligar um conjunto de uma vez. */
  combinacoesDeCorte: CombinacaoDeCorte[]
  /** Preencher a cheio os elementos cortados (caixa e planos).
   *  Preferência guardada por browser. */
  preenchimentoDeCorte: boolean
  preenchimentoInfo: PreenchimentoInfo | null
  /** Fundo e trama de tudo o que nenhuma regra apanha. Preferência. */
  estiloBaseDeCorte: EstiloDeCorte
  /** Regras por classe ou material, por ordem (a primeira que apanha
   *  ganha). Trabalho: vão na sessão. */
  regrasDeCorte: RegraDeCorte[]

  setSectionEnabled: (v: boolean) => void
  setSectionAlignMode: (v: boolean) => void
  setSectionPanel: (v: SectionPanelState) => void
  setSectionMostrarCaixa: (v: boolean) => void
  setSectionPanelDestacado: (v: boolean) => void
  /** O atalho: só faz alguma coisa com a caixa ligada (devolve false
   *  quando não fez, para a tecla seguir o seu caminho). */
  alternarMostrarCaixa: () => boolean
  /** O botão "Section box" da Vista (e o atalho): com a caixa desligada,
   *  liga-a; ligada com o painel fechado, reabre o painel; ligada com o
   *  painel à vista (aberto ou minimizado), desliga-a. */
  alternarSectionBox: () => void
  setSectionOrientation: (o: SectionOrientation) => void
  setSectionExtents: (e: SectionLocalExtents) => void
  setSectionExtentRange: (r: SectionLocalExtents | null) => void
  // Wipes orientation/extents/range so the section box re-derives
  // from the active model bbox (axis-aligned reset).
  resetSection: () => void

  acrescentarPlano: (p: PlanoDeCorte) => void
  acrescentarPlanoDoEixo: (eixo: Eixo, ponto: [number, number, number]) => void
  actualizarPlano: (id: string, muda: Partial<PlanoDeCorte>) => void
  tirarPlano: (id: string) => void
  /** Tudo, travados incluídos: é o "Fechar sessão". */
  tirarTodosOsPlanos: () => void
  /** O "Tirar todos" do painel: os travados ficam. */
  tirarPlanosDestravados: () => void
  /** Guarda os planos ligados agora como uma combinação; devolve o id
   *  (null se não há nenhum ligado). */
  guardarCombinacao: (nome?: string) => string | null
  aplicarCombinacaoDeCorte: (id: string) => void
  renomearCombinacao: (id: string, nome: string) => void
  tirarCombinacao: (id: string) => void
  setPreenchimentoDeCorte: (v: boolean) => void
  setPreenchimentoInfo: (v: PreenchimentoInfo | null) => void
  setEstiloBaseDeCorte: (muda: Partial<EstiloDeCorte>) => void
  acrescentarRegraDeCorte: (tipo: TipoDeRegra) => void
  actualizarRegraDeCorte: (id: string, muda: Partial<Omit<RegraDeCorte, 'id'>>) => void
  tirarRegraDeCorte: (id: string) => void
  /** Sobe (-1) ou desce (+1) uma regra na lista: a ordem decide. */
  moverRegraDeCorte: (id: string, sentido: -1 | 1) => void
  /** Junta as regras sugeridas (betão, alvenaria, isolamento, aço,
   *  madeira, vidro) ao fim da lista. */
  juntarRegrasSugeridas: () => void
  /** Repõe planos, combinações e regras de uma vez (sessão aberta). */
  reporCortes: (o: {
    planos: PlanoDeCorte[]
    combinacoes: CombinacaoDeCorte[]
    regras: RegraDeCorte[]
  }) => void
  setPlanoPorFace: (v: boolean) => void
  /** Quantos planos daquele eixo já existem, para dar nome ao próximo. */
  contarPlanosDoEixo: (eixo: Eixo) => number
}

export const IDENTITY_ORIENTATION: SectionOrientation = {
  origin: [0, 0, 0],
  axisX: [1, 0, 0],
  axisY: [0, 1, 0],
  axisZ: [0, 0, 1],
}

export const createSectionSlice: StateCreator<SectionSlice> = (set, get) => ({
  sectionEnabled: false,
  sectionOrientation: null,
  sectionExtents: null,
  sectionExtentRange: null,
  sectionAlignMode: false,
  sectionPanel: lerJanela().minimizado ? 'minimizado' : 'aberto',
  sectionMostrarCaixa: lerMostrarCaixa(),
  sectionPanelDestacado: false,
  planosDeCorte: [],
  planoPorFace: false,
  combinacoesDeCorte: [],
  preenchimentoDeCorte: lerPreenchimento(),
  preenchimentoInfo: null,
  estiloBaseDeCorte: lerEstiloBase(),
  regrasDeCorte: [],

  setSectionEnabled: (v) =>
    set((state) => ({
      sectionEnabled: v,
      // Toggling off also exits align mode.
      sectionAlignMode: v ? state.sectionAlignMode : false,
      // Desligar a caixa fecha a janela destacada e esquece-a: voltar a
      // ligar pode vir de um viewpoint (sem gesto da utilizadora), e o
      // browser bloqueava a janela.
      sectionPanelDestacado: v ? state.sectionPanelDestacado : false,
      // Ligar a caixa (pela Vista, por um viewpoint, pelo 2D) mostra o
      // painel se ele tinha sido fechado; minimizado fica minimizado.
      sectionPanel:
        v && !state.sectionEnabled && state.sectionPanel === 'fechado'
          ? 'aberto'
          : state.sectionPanel,
    })),
  setSectionAlignMode: (v) => set({ sectionAlignMode: v }),
  setSectionPanel: (v) => set({ sectionPanel: v }),
  setSectionPanelDestacado: (v) => set({ sectionPanelDestacado: v }),
  setSectionMostrarCaixa: (v) => {
    gravarMostrarCaixa(v)
    set({ sectionMostrarCaixa: v })
  },
  alternarMostrarCaixa: () => {
    const s = get()
    if (!s.sectionEnabled) return false
    s.setSectionMostrarCaixa(!s.sectionMostrarCaixa)
    return true
  },
  alternarSectionBox: () => {
    const s = get()
    if (!s.sectionEnabled) s.setSectionEnabled(true)
    else if (s.sectionPanel === 'fechado') set({ sectionPanel: 'aberto' })
    else s.setSectionEnabled(false)
  },
  setSectionOrientation: (o) => set({ sectionOrientation: o }),
  setSectionExtents: (e) => set({ sectionExtents: e }),
  setSectionExtentRange: (r) => set({ sectionExtentRange: r }),
  resetSection: () =>
    set({
      sectionOrientation: null,
      sectionExtents: null,
      sectionExtentRange: null,
      sectionAlignMode: false,
    }),

  acrescentarPlano: (p) =>
    set((state) => ({
      planosDeCorte: [...state.planosDeCorte, p],
      // Criar um plano fecha o modo de apanhar faces: quem clicou numa
      // parede quer UM corte, e não um por cada clique seguinte.
      planoPorFace: false,
    })),

  acrescentarPlanoDoEixo: (eixo, ponto) =>
    set((state) => {
      const quantos = state.planosDeCorte.filter((p) =>
        p.nome.startsWith(`Corte ${ROTULO_EIXO[eixo]}`),
      ).length
      return {
        planosDeCorte: [
          ...state.planosDeCorte,
          doEixo(eixo, ponto, crypto.randomUUID(), quantos + 1),
        ],
      }
    }),

  actualizarPlano: (id, muda) =>
    set((state) => ({
      // Travado, a posição e a direcção não mudam, venha o pedido de
      // onde vier (pega, campo, atalho).
      planosDeCorte: state.planosDeCorte.map((p) =>
        p.id === id ? { ...p, ...alteracaoPermitida(p, muda) } : p,
      ),
    })),

  tirarPlano: (id) =>
    set((state) => ({
      planosDeCorte: state.planosDeCorte.filter((p) => p.id !== id),
      combinacoesDeCorte: semOPlano(state.combinacoesDeCorte, [id]),
    })),

  // É o "Fechar sessão": as regras de estilo são trabalho da sessão e
  // saem com os planos.
  tirarTodosOsPlanos: () =>
    set({ planosDeCorte: [], planoPorFace: false, combinacoesDeCorte: [], regrasDeCorte: [] }),

  tirarPlanosDestravados: () =>
    set((state) => {
      const saem = state.planosDeCorte.filter((p) => !p.travado).map((p) => p.id)
      return {
        planosDeCorte: state.planosDeCorte.filter((p) => p.travado),
        planoPorFace: false,
        combinacoesDeCorte: semOPlano(state.combinacoesDeCorte, saem),
      }
    }),

  guardarCombinacao: (nome) => {
    const s = get()
    const planos = s.planosDeCorte.filter((p) => p.activo).map((p) => p.id)
    if (planos.length === 0) return null
    const id = crypto.randomUUID()
    const limpo = nome?.trim()
    set({
      combinacoesDeCorte: [
        ...s.combinacoesDeCorte,
        { id, nome: limpo || nomeDaCombinacao(s.combinacoesDeCorte), planos },
      ],
    })
    return id
  },

  aplicarCombinacaoDeCorte: (id) =>
    set((state) => {
      const c = state.combinacoesDeCorte.find((x) => x.id === id)
      if (!c) return {}
      return { planosDeCorte: aplicarCombinacao(state.planosDeCorte, c) }
    }),

  renomearCombinacao: (id, nome) =>
    set((state) => ({
      combinacoesDeCorte: state.combinacoesDeCorte.map((c) =>
        c.id === id ? { ...c, nome } : c,
      ),
    })),

  tirarCombinacao: (id) =>
    set((state) => ({
      combinacoesDeCorte: state.combinacoesDeCorte.filter((c) => c.id !== id),
    })),

  setPreenchimentoDeCorte: (v) => {
    gravarPreenchimento(v)
    set({ preenchimentoDeCorte: v, ...(v ? {} : { preenchimentoInfo: null }) })
  },
  setPreenchimentoInfo: (v) => set({ preenchimentoInfo: v }),

  setEstiloBaseDeCorte: (muda) => {
    const novo = { ...get().estiloBaseDeCorte, ...muda }
    gravarEstiloBase(novo)
    set({ estiloBaseDeCorte: novo })
  },
  acrescentarRegraDeCorte: (tipo) =>
    set((state) => ({
      regrasDeCorte: [
        ...state.regrasDeCorte,
        {
          id: crypto.randomUUID(),
          activa: true,
          tipo,
          // A de lente começa na primeira lente guardada, se houver.
          valor: tipo === 'classe' ? 'IfcSlab' : tipo === 'lente' ? (lerLentes()[0]?.id ?? '') : '',
          estilo: { ...ESTILO_BASE_POR_OMISSAO, fundo: '#c8ccd2' },
        },
      ],
    })),
  actualizarRegraDeCorte: (id, muda) =>
    set((state) => ({
      regrasDeCorte: state.regrasDeCorte.map((r) =>
        r.id === id ? { ...r, ...muda, estilo: { ...r.estilo, ...muda.estilo } } : r,
      ),
    })),
  tirarRegraDeCorte: (id) =>
    set((state) => ({ regrasDeCorte: state.regrasDeCorte.filter((r) => r.id !== id) })),
  moverRegraDeCorte: (id, sentido) =>
    set((state) => {
      const i = state.regrasDeCorte.findIndex((r) => r.id === id)
      const j = i + sentido
      if (i < 0 || j < 0 || j >= state.regrasDeCorte.length) return {}
      const l = [...state.regrasDeCorte]
      ;[l[i], l[j]] = [l[j], l[i]]
      return { regrasDeCorte: l }
    }),
  juntarRegrasSugeridas: () =>
    set((state) => ({
      regrasDeCorte: [
        ...state.regrasDeCorte,
        ...REGRAS_SUGERIDAS.map((r) => ({ ...r, estilo: { ...r.estilo }, id: crypto.randomUUID() })),
      ],
    })),
  reporCortes: ({ planos, combinacoes, regras }) =>
    set({ planosDeCorte: planos, combinacoesDeCorte: combinacoes, regrasDeCorte: regras, planoPorFace: false }),

  setPlanoPorFace: (v) => set({ planoPorFace: v }),

  contarPlanosDoEixo: (eixo) =>
    get().planosDeCorte.filter((p) =>
      p.nome.startsWith(`Corte ${ROTULO_EIXO[eixo]}`),
    ).length,
})

/** As combinações sem estes planos; a que ficar vazia sai. */
function semOPlano(
  combinacoes: CombinacaoDeCorte[],
  ids: readonly string[],
): CombinacaoDeCorte[] {
  if (ids.length === 0) return combinacoes
  const saem = new Set(ids)
  return combinacoes
    .map((c) => ({ ...c, planos: c.planos.filter((p) => !saem.has(p)) }))
    .filter((c) => c.planos.length > 0)
}
